import { Users, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSeatUsage, type SeatUsage } from '@/hooks/useEnterpriseSeatUsage';
import { cn } from '@/lib/utils';

/**
 * Seat-usage stat for the enterprise Members page (#609, Phase 1).
 *
 * Renders "{used} / {licensed} seats" for a `licensed` partner (with an
 * "At limit" chip when at_cap), else "{used} seats" for per_seat / unconfigured
 * partners (no denominator). Matches the AnalyticsKPITile card house style.
 */

/** Pure label derivation — tested directly across all billing models. */
export function seatLabel(usage: SeatUsage): string {
  if (usage.billing_model === 'licensed' && usage.licensed_seats !== null) {
    return `${usage.seats_used} / ${usage.licensed_seats} seats`;
  }
  return `${usage.seats_used} ${usage.seats_used === 1 ? 'seat' : 'seats'}`;
}

interface SeatUsageStatProps {
  partnerId: string;
}

export function SeatUsageStat({ partnerId }: SeatUsageStatProps): JSX.Element {
  const { data, isLoading, error, refetch } = useSeatUsage(partnerId);

  if (error) {
    return (
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">Seats</div>
          <Users className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="mt-2 flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" />
          <span>Couldn&rsquo;t load seat usage.</span>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const atCap = data?.at_cap ?? false;

  return (
    <Card className={cn('p-4', atCap && 'border-destructive/50')}>
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Seats</div>
        <Users className="h-4 w-4 text-muted-foreground" />
      </div>
      {isLoading || !data ? (
        <Skeleton className="mt-2 h-7 w-28" />
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <div className="text-2xl font-bold tabular-nums">{seatLabel(data)}</div>
          {atCap && (
            <Badge variant="destructive" className="gap-1 font-normal">
              <AlertTriangle className="h-3 w-3" />
              At limit
            </Badge>
          )}
        </div>
      )}
    </Card>
  );
}
